import { useQuery } from '@tanstack/react-query';

export interface BitcoinBlock {
  id: string;
  height: number;
  version: number;
  timestamp: number;
  tx_count: number;
  size: number;
  weight: number;
  merkle_root: string;
  previousblockhash: string;
  mediantime: number;
  nonce: number;
  bits: number;
  difficulty: number;
}

export interface MempoolInfo {
  count: number;
  vsize: number;
  total_fee: number;
  fee_histogram: number[][];
}

export function useBitcoinBlocks() {
  return useQuery({
    queryKey: ['bitcoin-blocks'],
    queryFn: async (c) => {
      const signal = AbortSignal.any([c.signal, AbortSignal.timeout(10000)]);
      
      const response = await fetch('https://mempool.space/api/v1/blocks', { signal });
      
      if (!response.ok) {
        throw new Error('Failed to fetch Bitcoin blocks');
      }
      
      const blocks: BitcoinBlock[] = await response.json();
      return blocks;
    },
    refetchInterval: 30000, // Refetch every 30 seconds
  });
}

export function useMempoolInfo() {
  return useQuery({
    queryKey: ['mempool-info'],
    queryFn: async (c) => {
      const signal = AbortSignal.any([c.signal, AbortSignal.timeout(10000)]);
      
      const response = await fetch('https://mempool.space/api/mempool', { signal });
      
      if (!response.ok) {
        throw new Error('Failed to fetch mempool info');
      }
      
      const mempool: MempoolInfo = await response.json();
      return mempool;
    },
    refetchInterval: 10000, // Refetch every 10 seconds
  });
}

export function useBitcoinPrice() {
  return useQuery({
    queryKey: ['bitcoin-price'],
    queryFn: async (c) => {
      const signal = AbortSignal.any([c.signal, AbortSignal.timeout(10000)]);
      
      const response = await fetch('https://mempool.space/api/v1/prices', { signal });
      
      if (!response.ok) {
        throw new Error('Failed to fetch Bitcoin price');
      }
      
      const prices = await response.json();
      return prices.USD as number;
    },
    refetchInterval: 60000, // Refetch every minute
  });
}

export function useBlockDetails(blockHash: string) {
  return useQuery({
    queryKey: ['block-details', blockHash],
    queryFn: async (c) => {
      const signal = AbortSignal.any([c.signal, AbortSignal.timeout(10000)]);
      
      const response = await fetch(`https://mempool.space/api/block/${blockHash}`, { signal });
      
      if (!response.ok) {
        throw new Error('Failed to fetch block details');
      }
      
      const block = await response.json();
      return block;
    },
    enabled: !!blockHash,
  });
}
